/**
 * /orgs/:orgId/config — the tenant's plugin configuration, in two tabs.
 *
 * **Configure** is what you may change; **Resolved** is what will actually run.
 * That difference is the whole page: a field locked to the global layer is
 * hidden in Configure, because you cannot touch it, and shown in Resolved,
 * because it still decides the outcome.
 *
 * Trigger policies, once a second band here, were retired by Feature 77; the
 * index holds the capabilities.
 *
 * Selection lives here rather than inside the index so that opening a card
 * takes over the tab, instead of leaving the other cards stranded below an
 * open editor.
 */
import { listCapabilities } from '@optum/api-client/endpoints/capabilities';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { qk } from '../../lib/query-keys.js';
import { useRequiredOrgId } from '../../lib/use-current-org.js';
import { Band } from '../../ui/Band.js';
import { ListShape } from '../../ui/shapes.js';
import { Tabs, type TabItem } from '../../ui/Tabs.js';
import { Icon } from '../../ui/finixt.js';
import { CapabilityConfigForm } from './CapabilityConfigForm.js';
import { ConfigCardIndex, type ConfigItem } from './ConfigCardIndex.js';
import { ResolvedConfigTab } from './ResolvedConfigTab.js';

type ConfigTab = 'configure' | 'resolved';

const TABS: TabItem<ConfigTab>[] = [
  { value: 'configure', label: 'Configure' },
  { value: 'resolved', label: 'Resolved' },
];

const SUBTITLE: Record<ConfigTab, string> = {
  configure:
    'Override the platform defaults for this tenant. Fields locked to global are not shown.',
  resolved: 'What four layers actually produce for this tenant and user, before a task proves it.',
};

export function OrgConfigPage() {
  const orgId = useRequiredOrgId();
  const [tab, setTab] = useState<ConfigTab>('configure');
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <ListShape title="Capability configuration" subtitle={SUBTITLE[tab]}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 64 }}>
        <Tabs
          items={TABS}
          value={tab}
          onChange={(next) => {
            setTab(next);
            setSelected(null);
          }}
          style={{ marginBottom: 16 }}
        />
        {tab === 'configure' &&
          (selected ? (
            <ConfigEditor
              organizationId={orgId}
              capabilityId={selected}
              onBack={() => setSelected(null)}
            />
          ) : (
            <ConfigIndex organizationId={orgId} onSelect={setSelected} />
          ))}
        {tab === 'resolved' && <ResolvedConfigTab organizationId={orgId} />}
      </div>
    </ListShape>
  );
}

function ConfigIndex({
  organizationId,
  onSelect,
}: {
  organizationId: string;
  onSelect: (capabilityId: string) => void;
}) {
  const caps = useQuery({ queryKey: qk.capabilities(), queryFn: listCapabilities });

  const activeCaps = useMemo<ConfigItem[]>(
    () =>
      (caps.data ?? [])
        .filter((c) => c.status === 'active')
        .map((c) => ({ id: c.id, name: c.name, version: c.version })),
    [caps.data],
  );
  if (caps.isPending) return <LoadingBlock />;
  if (caps.isError) {
    return (
      <ErrorBlock
        message={caps.error instanceof Error ? caps.error.message : 'Failed to load capabilities'}
      />
    );
  }

  return (
    <>
      <Band
        label="Capabilities"
        meta={`${activeCaps.length} active`}
        style={{ marginBottom: 11 }}
      />
      {activeCaps.length === 0 ? (
        <EmptyBlock>No active capabilities to configure.</EmptyBlock>
      ) : (
        <ConfigCardIndex organizationId={organizationId} items={activeCaps} onSelect={onSelect} />
      )}
    </>
  );
}

function ConfigEditor({
  organizationId,
  capabilityId,
  onBack,
}: {
  organizationId: string;
  capabilityId: string;
  onBack: () => void;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        style={{
          marginBottom: 16,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          fontSize: 'var(--text-body-md)',
          color: 'var(--text-muted)',
        }}
      >
        <Icon name="arrow_back" size={15} />
        All capabilities
      </button>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 11 }}>
        <span
          style={{
            display: 'flex',
            width: 32,
            height: 32,
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'var(--radius-input)',
            background: 'var(--blue-tint)',
            color: 'var(--blue)',
          }}
        >
          <Icon name="extension" size={15} />
        </span>
        <span
          style={{
            fontSize: 'var(--text-body-lg)',
            fontWeight: 'var(--weight-bold)',
            color: 'var(--text-strong)',
          }}
          className="mono"
        >
          {capabilityId}
        </span>
      </div>
      <CapabilityConfigForm organizationId={organizationId} capabilityId={capabilityId} />
    </div>
  );
}
